import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Power, Percent, TicketPercent, MailWarning } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30";

export default function AdminMarketing({ cfg }) {
  const [storeOpen, setStoreOpen] = useState(null);
  const [togglingStore, setTogglingStore] = useState(false);
  const [sale, setSale] = useState(null);
  const [saleLive, setSaleLive] = useState(false);
  const [saleForm, setSaleForm] = useState({ percent: "20", ends_at: "" });
  const [coupons, setCoupons] = useState(null);
  const [couponForm, setCouponForm] = useState({ code: "", percent_off: "10" });
  const [blast, setBlast] = useState({ subject: "", message: "" });
  const [blastStats, setBlastStats] = useState(null);
  const [sendingBlast, setSendingBlast] = useState(false);
  const [confirmBlast, setConfirmBlast] = useState(false);
  const [subs, setSubs] = useState(null);
  const [pruning, setPruning] = useState(null);

  const loadAll = async () => {
    try {
      const [s, sa, c, n, sub] = await Promise.all([
        axios.get(`${API}/admin/store`, cfg()),
        axios.get(`${API}/admin/sale`, cfg()),
        axios.get(`${API}/admin/coupons`, cfg()),
        axios.get(`${API}/admin/newsletter/stats`, cfg()),
        axios.get(`${API}/admin/newsletter/subscribers`, cfg()),
      ]);
      setStoreOpen(s.data.open);
      setSale(sa.data.sale);
      setSaleLive(sa.data.live);
      setCoupons(c.data);
      setBlastStats(n.data);
      setSubs(sub.data);
    } catch {
      toast.error("Couldn't load the marketing crypt.");
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleStore = async () => {
    setTogglingStore(true);
    try {
      const r = await axios.post(`${API}/admin/store`, { open: !storeOpen }, cfg());
      setStoreOpen(r.data.open);
      toast.success(r.data.open ? "The crypt shop is OPEN." : "Shop shuttered — visitors see the construction notice.");
    } catch {
      toast.error("The switch stuck. Try again.");
    } finally {
      setTogglingStore(false);
    }
  };

  const startSale = async (e) => {
    e.preventDefault();
    try {
      const ends = new Date(saleForm.ends_at).toISOString();
      await axios.post(`${API}/admin/sale`, { percent: parseInt(saleForm.percent, 10), ends_at: ends }, cfg());
      toast.success(`Sale live — ${saleForm.percent}% off everything until it ends.`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start the sale.");
    }
  };

  const endSale = async () => {
    try {
      await axios.delete(`${API}/admin/sale`, cfg());
      toast.success("Sale buried. Prices back to full blood.");
      loadAll();
    } catch {
      toast.error("Couldn't end the sale.");
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(
        `${API}/admin/coupons`,
        { code: couponForm.code, percent_off: parseInt(couponForm.percent_off, 10) },
        cfg()
      );
      toast.success(`${r.data.code} is live — fans can use it at checkout.`);
      setCouponForm({ code: "", percent_off: "10" });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Stripe refused that code.");
    }
  };

  const killCoupon = async (c) => {
    try {
      await axios.delete(`${API}/admin/coupons/${c.id}`, cfg());
      toast.success(`${c.code} is dead.`);
      loadAll();
    } catch {
      toast.error("Couldn't kill that code.");
    }
  };

  const sendBlast = async () => {
    setSendingBlast(true);
    try {
      const r = await axios.post(`${API}/admin/blast`, blast, cfg());
      toast.success(`Blast sent — ${r.data.sent}/${r.data.recipients} covens reached.`);
      setBlast({ subject: "", message: "" });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "The blast fizzled.");
    } finally {
      setSendingBlast(false);
      setConfirmBlast(false);
    }
  };

  return (
    <div data-testid="admin-marketing-panel" className="space-y-8">
      <div className="border border-blooddeep/50 bg-pulp p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-impact uppercase tracking-wider text-sm text-paper flex items-center gap-2">
              <Power size={15} className="text-bloodglow" /> Store Switch
            </h3>
            <p className="font-type text-xs text-paper/50 mt-1">
              Closed shop shows "Store is currently under construction, please check back later." and blocks checkout.
            </p>
          </div>
          <button
            data-testid="store-toggle-button"
            onClick={toggleStore}
            disabled={togglingStore || storeOpen === null}
            className={`font-impact uppercase tracking-wider text-sm border-2 border-black px-6 py-3 ink-shadow-sm transition-colors duration-150 disabled:opacity-50 ${
              storeOpen ? "bg-crimson text-white hover:bg-blooddeep" : "bg-yell text-black hover:bg-yell/80"
            }`}
          >
            {storeOpen === null ? "..." : storeOpen ? "OPEN — Click to Close" : "CLOSED — Click to Open"}
          </button>
        </div>
      </div>

      <div className="border border-blooddeep/50 bg-pulp p-6">
        <h3 className="font-impact uppercase tracking-wider text-sm text-paper flex items-center gap-2 mb-1">
          <Percent size={15} className="text-bloodglow" /> Site-Wide Sale
        </h3>
        <p className="font-type text-xs text-paper/50 mb-4">
          Slashes every price in the shop until the end time. Checkout charges the sale price automatically.
        </p>
        {saleLive && sale ? (
          <div className="flex flex-wrap items-center gap-4">
            <p data-testid="sale-live-status" className="font-marker text-yell text-sm">
              LIVE: {sale.percent}% off — ends {new Date(sale.ends_at).toLocaleString()}
            </p>
            <button
              data-testid="sale-end-button"
              onClick={endSale}
              className="font-impact uppercase tracking-wider text-xs text-bloodglow border border-blooddeep/50 px-4 py-2 transition-colors duration-150 hover:border-crimson hover:text-crimson"
            >
              End Sale
            </button>
          </div>
        ) : (
          <form data-testid="sale-form" onSubmit={startSale} className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="font-courier text-[10px] uppercase tracking-widest text-paper/40 block mb-1">% off</span>
              <input
                data-testid="sale-percent-input"
                type="number"
                min="1"
                max="90"
                value={saleForm.percent}
                onChange={(e) => setSaleForm({ ...saleForm, percent: e.target.value })}
                className={`${inputCls} w-24`}
                required
              />
            </label>
            <label className="block">
              <span className="font-courier text-[10px] uppercase tracking-widest text-paper/40 block mb-1">Ends at</span>
              <input
                data-testid="sale-ends-input"
                type="datetime-local"
                value={saleForm.ends_at}
                onChange={(e) => setSaleForm({ ...saleForm, ends_at: e.target.value })}
                className={inputCls}
                required
              />
            </label>
            <button
              data-testid="sale-start-button"
              className="font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-5 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson"
            >
              Start Sale
            </button>
          </form>
        )}
      </div>

      <div className="border border-blooddeep/50 bg-pulp p-6">
        <h3 className="font-impact uppercase tracking-wider text-sm text-paper flex items-center gap-2 mb-1">
          <TicketPercent size={15} className="text-bloodglow" /> Coupon Codes
        </h3>
        <p className="font-type text-xs text-paper/50 mb-4">
          Fans type these at Stripe checkout. Each code works once per customer.
        </p>
        <form data-testid="coupon-form" onSubmit={createCoupon} className="flex flex-wrap items-end gap-3 mb-5">
          <label className="block">
            <span className="font-courier text-[10px] uppercase tracking-widest text-paper/40 block mb-1">Code</span>
            <input
              data-testid="coupon-code-input"
              value={couponForm.code}
              onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
              placeholder="BLOOD10"
              className={`${inputCls} w-40 uppercase`}
              required
            />
          </label>
          <label className="block">
            <span className="font-courier text-[10px] uppercase tracking-widest text-paper/40 block mb-1">% off</span>
            <input
              data-testid="coupon-percent-input"
              type="number"
              min="1"
              max="90"
              value={couponForm.percent_off}
              onChange={(e) => setCouponForm({ ...couponForm, percent_off: e.target.value })}
              className={`${inputCls} w-24`}
              required
            />
          </label>
          <button
            data-testid="coupon-create-button"
            className="font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-5 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson"
          >
            Forge Code
          </button>
        </form>
        <div className="space-y-2">
          {(coupons || []).filter((c) => c.active).map((c) => (
            <div
              key={c.id}
              data-testid={`coupon-row-${c.code}`}
              className={`flex items-center justify-between border px-4 py-2.5 ${c.active ? "border-blooddeep/40 bg-void/50" : "border-paper/10 bg-void/30 opacity-50"}`}
            >
              <span className="font-courier text-sm text-paper">
                {c.code} <span className="text-paper/40">— {c.percent_off}% off</span>
              </span>
              {c.active ? (
                <button
                  data-testid={`coupon-kill-${c.code}`}
                  onClick={() => killCoupon(c)}
                  className="font-impact uppercase tracking-wider text-xs text-bloodglow border border-blooddeep/50 px-3 py-1.5 transition-colors duration-150 hover:border-crimson hover:text-crimson"
                >
                  Kill
                </button>
              ) : (
                <span className="font-courier text-[10px] uppercase tracking-widest text-paper/35">dead</span>
              )}
            </div>
          ))}
          {coupons && coupons.filter((c) => c.active).length === 0 && <p className="font-type text-xs text-paper/50">No live codes. Forge one above.</p>}
        </div>
      </div>

      <div className="border border-blooddeep/50 bg-pulp p-6">
        <h3 className="font-impact uppercase tracking-wider text-sm text-paper flex items-center gap-2 mb-1">
          <MailWarning size={15} className="text-bloodglow" /> Newsletter Blast
        </h3>
        <p className="font-type text-xs text-paper/50 mb-4">
          One message, every subscriber — <span data-testid="blast-subscriber-count" className="text-yell">{blastStats ? blastStats.subscribers : "…"}</span> in the coven right now.
        </p>
        <form
          data-testid="blast-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!confirmBlast) {
              setConfirmBlast(true);
              setTimeout(() => setConfirmBlast(false), 5000);
              return;
            }
            sendBlast();
          }}
          className="space-y-3"
        >
          <input
            data-testid="blast-subject-input"
            value={blast.subject}
            onChange={(e) => setBlast({ ...blast, subject: e.target.value })}
            placeholder="Subject line"
            maxLength={140}
            className={`${inputCls} w-full`}
            required
          />
          <textarea
            data-testid="blast-message-input"
            value={blast.message}
            onChange={(e) => setBlast({ ...blast, message: e.target.value })}
            placeholder="The word. Blank lines split paragraphs."
            rows={5}
            maxLength={5000}
            className={`${inputCls} w-full resize-none`}
            required
          />
          <button
            data-testid="blast-send-button"
            disabled={sendingBlast}
            className={`font-impact uppercase tracking-wider text-xs border-2 border-black px-5 py-2.5 ink-shadow-sm transition-colors duration-150 disabled:opacity-50 ${
              confirmBlast ? "bg-crimson text-white" : "bg-blooddeep text-white hover:bg-crimson"
            }`}
          >
            {sendingBlast ? "Unleashing..." : confirmBlast ? "Sure? This emails EVERYONE" : "Unleash Blast"}
          </button>
        </form>
        {blastStats?.blasts?.length > 0 && (
          <div className="mt-5 pt-4 border-t border-blooddeep/40 space-y-2">
            {blastStats.blasts.map((b) => (
              <p key={b.id} data-testid={`blast-row-${b.id}`} className="font-courier text-[11px] text-paper/50">
                {new Date(b.created_at).toLocaleDateString()} — "{b.subject}" — {b.sent}/{b.recipients} sent
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="border border-blooddeep/50 bg-pulp p-6">
        <h3 className="font-impact uppercase tracking-wider text-sm text-paper mb-1">The Coven Roll</h3>
        <p className="font-type text-xs text-paper/50 mb-4">Every newsletter subscriber. Prune the dead weight.</p>
        {!subs ? (
          <p className="font-type text-xs text-paper/50">Summoning...</p>
        ) : subs.length === 0 ? (
          <p className="font-type text-xs text-paper/50">No subscribers yet.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {subs.map((s) => (
              <div key={s.email} data-testid={`subscriber-row-${s.email}`} className="flex items-center justify-between border border-blooddeep/40 bg-void/50 px-4 py-2.5">
                <div>
                  <p className="font-courier text-sm text-paper">{s.email}</p>
                  <p className="font-courier text-[10px] text-paper/40">joined {new Date(s.created_at).toLocaleDateString()}</p>
                </div>
                <button
                  data-testid={`subscriber-prune-${s.email}`}
                  onClick={async () => {
                    if (pruning === s.email) {
                      try {
                        await axios.delete(`${API}/admin/newsletter/subscribers/${encodeURIComponent(s.email)}`, cfg());
                        toast.success(`${s.email} pruned from the coven.`);
                        loadAll();
                      } catch {
                        toast.error("Couldn't prune that one.");
                      } finally {
                        setPruning(null);
                      }
                    } else {
                      setPruning(s.email);
                      setTimeout(() => setPruning((cur) => (cur === s.email ? null : cur)), 4000);
                    }
                  }}
                  className={`font-impact uppercase tracking-wider text-[11px] border px-3 py-1.5 transition-colors duration-150 ${
                    pruning === s.email ? "bg-blooddeep text-white border-black" : "text-bloodglow border-blooddeep/50 hover:border-crimson hover:text-crimson"
                  }`}
                >
                  {pruning === s.email ? "Sure?" : "Prune"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
